import { getErrorOccurrenceId } from './errorOccurrence.js';
// Only engine-owned getters are invoked; provider accessors and toJSON never are. V8 may expose
// the lazy stack as an own accessor, SpiderMonkey keeps `stack` on Error.prototype, and
// DOMException (AbortError, NotAllowedError, ...) keeps name/message/code on its prototype.
const engineGetters = new Map();
function trustGetter(owner, key) {
    const get = owner && Object.getOwnPropertyDescriptor(owner, key)?.get;
    if (!get)
        return;
    const getters = engineGetters.get(key) ?? new Set();
    engineGetters.set(key, getters.add(get));
}
trustGetter(new Error(), 'stack');
trustGetter(Error.prototype, 'stack');
if (typeof DOMException === 'function')
    for (const key of ['name', 'message', 'code'])
        trustGetter(DOMException.prototype, key);
function read(target, key) {
    try {
        let owner = target;
        for (let depth = 0; owner && typeof owner === 'object' && depth < 8; depth++) {
            const property = Object.getOwnPropertyDescriptor(owner, key);
            if (property) {
                if ('value' in property)
                    return property.value;
                return property.get && engineGetters.get(key)?.has(property.get) ? property.get.call(target) : undefined;
            }
            owner = Object.getPrototypeOf(owner);
        }
    }
    catch { /* Foreign proxies can throw; reporting remains best effort. */ }
    return undefined;
}
/** Redacts credential-shaped key/value pairs; wallet signatures are replay-bound, not credentials. */
function clean(value) {
    if (typeof value !== 'string')
        return undefined;
    return value.replace(/(bearer\s+)[a-z0-9._~+/=-]+/gi, '$1[REDACTED]')
        .replace(/((?:authorization|cookie|api[_-]?key|x-ls-apikey|access[_-]?token|refresh[_-]?token|private[_-]?key|password|secret)["']?\s*[:=]\s*)("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|[^,&}\n]+)/gi, '$1[REDACTED]')
        .slice(0, 8192);
}
const URL_PARTS = /^([a-z][a-z0-9+.-]*:\/\/)(?:[^/?#@\s]*@)?([^/?#\s]*)(.*)$/is;
const CODE_LOCATION = /(?::\d+){1,2}$/;
/**
 * Provider URLs (RPC nodes) often carry an API key in the path, so only scheme and host
 * are diagnostic. A stack-frame location (`.../chunk.js:12:34`) points at code and keeps
 * its path. Query, fragment and userinfo never survive.
 */
function providerUrl(raw) {
    const parts = URL_PARTS.exec(raw);
    if (!parts)
        return raw.split(/[?#]/, 1)[0];
    const [, scheme, host, rest] = parts;
    const location = CODE_LOCATION.exec(rest)?.[0];
    return scheme + host + (location ? rest.split(/[?#]/, 1)[0].replace(CODE_LOCATION, '') + location : '');
}
/** Free text: every embedded URL is treated as a provider URL. */
function text(value) {
    return clean(value)?.replace(/\b(?:https?|wss?):\/\/[^\s"'<>()[\]{}\\,]+/gi, providerUrl);
}
/** Layerswap API routes: the path identifies the call and carries no credentials. */
function apiUrl(value) {
    return clean(value)?.split(/[?#]/, 1)[0].replace(/((?:https?|wss?):\/\/)[^/\s]*@/gi, '$1');
}
function nodeUrl(value) {
    const result = clean(value);
    return result === undefined ? undefined : providerUrl(result);
}
function scalar(value) {
    return typeof value === 'number' && Number.isFinite(value) ? value : text(value);
}
/** Account for JSON escaping and field names, so a branching error fits one telemetry attribute. */
function summaryField(result, key, value, budget) {
    if (value === undefined)
        return;
    let cost = JSON.stringify(key).length + JSON.stringify(value).length + 2;
    while (cost > budget.remaining && typeof value === 'string' && value.length) {
        value = value.slice(0, Math.floor(value.length / 2));
        cost = JSON.stringify(key).length + JSON.stringify(value).length + 2;
    }
    if (cost > budget.remaining)
        return;
    budget.remaining -= cost;
    result[key] = value;
}
function summarizeErrorNode(value, depth, budget) {
    if (value == null || depth >= 8 || budget.nodes >= 16 || budget.remaining < 32)
        return undefined;
    if (typeof value === 'object') {
        if (budget.seen.has(value) || Array.isArray(value))
            return undefined;
        budget.seen.add(value);
    }
    budget.nodes++;
    budget.remaining -= 2; // Object braces.
    const result = {};
    if (typeof value !== 'object') {
        summaryField(result, 'message', text(value), budget);
        return Object.keys(result).length ? result : undefined;
    }
    for (const key of ['name', 'message', 'shortMessage', 'details', 'method', 'revert_reason', 'contract_address', 'class_hash', 'selector']) {
        summaryField(result, key, text(read(value, key)), budget);
    }
    for (const key of ['code', 'status']) {
        summaryField(result, key, scalar(read(value, key)), budget);
    }
    const transactionIndex = read(value, 'transaction_index');
    if (typeof transactionIndex === 'number' && Number.isSafeInteger(transactionIndex) && transactionIndex >= 0) {
        summaryField(result, 'transaction_index', transactionIndex, budget);
    }
    // Axios transport details are read explicitly; config/request/body never leave this boundary.
    const config = read(value, 'config');
    const method = text(read(config, 'method'));
    const status = scalar(read(read(value, 'response'), 'status'));
    if (result.method === undefined)
        summaryField(result, 'method', method, budget);
    if (result.status === undefined)
        summaryField(result, 'status', status, budget);
    const safeUrl = nodeUrl(read(value, 'url') ?? read(config, 'url'));
    summaryField(result, 'url', safeUrl, budget);
    // Known SDK wrappers and Starknet RPC execution traces only. Never copy request bodies,
    // signatures, arbitrary data keys, getters or toJSON from a provider object.
    for (const key of ['cause', 'error', 'data', 'originalError', 'info', 'revert_error', 'execution_error']) {
        const nested = read(value, key);
        if (nested == null)
            continue;
        // `data`/`info` may contain an opaque serialized request, not an error message.
        // Inspect their object fields only; execution-error string leaves remain useful.
        if ((key === 'data' || key === 'info') && typeof nested !== 'object')
            continue;
        budget.remaining -= key.length + 4; // Quoted key, colon, comma; children debit their own fields.
        const summary = summarizeErrorNode(nested, depth + 1, budget);
        if (summary)
            result[key] = summary;
    }
    // Prefer the structured failure to a verbose wrapper stack when nearing the budget.
    summaryField(result, 'stack', text(read(value, 'stack')), budget);
    return Object.keys(result).length ? result : undefined;
}
/** Allowlisted diagnostics with a shared 8,000-character JSON budget, node and depth limits. */
export function summarizeError(value, depth = 0) {
    return summarizeErrorNode(value, depth, { remaining: 8000, nodes: 0, seen: new WeakSet() });
}
function response(value) {
    if (!value || typeof value !== 'object')
        return undefined;
    const result = {};
    const code = scalar(read(value, 'code'));
    const message = text(read(value, 'message'));
    if (code !== undefined)
        result.code = code;
    if (message !== undefined)
        result.message = message;
    const error = read(value, 'error');
    if (error && typeof error === 'object') {
        // Response bodies contribute only the server's error code/message, never echoed requests.
        const summary = responseCodeAndMessage(error);
        if (Object.keys(summary).length)
            result.error = summary;
    }
    return Object.keys(result).length ? result : undefined;
}
function responseCodeAndMessage(value) {
    const result = {};
    const code = scalar(read(value, 'code'));
    const message = text(read(value, 'message'));
    if (code !== undefined)
        result.code = code;
    if (message !== undefined)
        result.message = message;
    return result;
}
const stringFields = ['occurrenceId', 'reasonCode', 'name', 'message', 'stack', 'statusText', 'requestMethod',
    'network', 'address', 'error_category', 'response_status_text', 'error_code', 'swapId', 'transactionHash',
    'fromAddress', 'toAddress', 'token', 'operation'];
/** Layerswap API routes keep their path; provider/RPC URLs keep only scheme and host. */
const apiUrlFields = ['endpoint', 'requestUrl'];
const providerUrlFields = ['node_url', 'request_url'];
const numberFields = ['requestedAmount', 'walletBalance', 'calculatedGas', 'difference', 'response_status'];
const arrayFields = ['error_categories', 'error_codes', 'http_statuses', 'nodes'];
function buildErrorReport(event) {
    const result = {
        type: (text(read(event, 'type')) ?? 'ErrorFallback'),
        message: text(read(event, 'message')) ?? 'Unknown error',
    };
    for (const key of stringFields) {
        const field = text(read(event, key));
        if (field !== undefined)
            result[key] = field;
    }
    for (const key of apiUrlFields) {
        const field = apiUrl(read(event, key));
        if (field !== undefined)
            result[key] = field;
    }
    for (const key of providerUrlFields) {
        const field = nodeUrl(read(event, key));
        if (field !== undefined)
            result[key] = field;
    }
    for (const key of numberFields) {
        const field = read(event, key);
        if (typeof field === 'number' && Number.isFinite(field))
            result[key] = field;
    }
    const status = scalar(read(event, 'status'));
    if (status !== undefined)
        result.status = status;
    for (const key of arrayFields) {
        const field = read(event, key);
        if (Array.isArray(field))
            result[key] = field.slice(0, 100).map(key === 'nodes' ? nodeUrl : scalar).filter(v => v !== undefined);
    }
    const cause = summarizeError(read(event, 'cause'));
    if (cause)
        result.cause = cause;
    for (const key of ['responseData', 'response_data']) {
        const summary = response(read(event, key));
        if (summary)
            result[key] = summary;
    }
    const failedTokens = read(event, 'failed_tokens');
    if (Array.isArray(failedTokens))
        result.failed_tokens = failedTokens.slice(0, 100).map(value => {
            const token = {};
            for (const key of ['token', 'error_message', 'error_name', 'error_category', 'response_status_text', 'error_stack']) {
                const field = text(read(value, key));
                if (field !== undefined)
                    token[key] = field;
            }
            const code = scalar(read(value, 'error_code'));
            if (code !== undefined)
                token.error_code = code;
            const status = read(value, 'response_status');
            if (typeof status === 'number' && Number.isFinite(status))
                token.response_status = status;
            const requestUrl = nodeUrl(read(value, 'request_url'));
            if (requestUrl !== undefined)
                token.request_url = requestUrl;
            const responseData = response(read(value, 'response_data'));
            if (responseData !== undefined)
                token.response_data = responseData;
            return token;
        });
    result.message ?? (result.message = 'Unknown error');
    result.occurrenceId ?? (result.occurrenceId = getErrorOccurrenceId(event));
    return result;
}
export function toErrorReport(event) {
    try {
        return buildErrorReport(event);
    }
    catch {
        // Even hostile proxies/array subclasses cannot make an optional report fail an operation.
        return { type: 'ErrorFallback', message: 'Error details unavailable', occurrenceId: getErrorOccurrenceId(event) };
    }
}
